import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import { t } from 'ember-intl';
import { Request } from 'tio-data';
import { notificationSnapshotRequest } from 'tio-data/requests/profile-history';
import type { NotificationHistoryExtension } from 'tio-data/data/resources/notification-history.ext';
import Modal from 'tio-ui/components/modal';

interface NotificationSnapshotPreviewSignature {
  Args: {
    /** Selected send; the parent keys this component by row ID and recreates it on reopen. */
    row: NotificationHistoryExtension;
    onClose: () => void;
  };
}

interface SnapshotModalSignature extends NotificationSnapshotPreviewSignature {
  Args: NotificationSnapshotPreviewSignature['Args'] & {
    snapshot?: NotificationHistoryExtension | null;
    isLoading?: boolean;
  };
}

function sandboxSnapshotHtml(html: string | null | undefined) {
  if (!html) return null;

  const meta = // An iframe sandbox alone still allows remote images and recipient tracking pixels.
    '<meta http-equiv="Content-Security-Policy" ' +
    `content="default-src 'none'; style-src 'unsafe-inline'; img-src data:">`;

  const head = html.match(/<head(?=[\s>])[^>]*>/i); // Do not match a body <header>; meta CSP is honored only in the head.
  if (head) return html.replace(head[0], `${head[0]}${meta}`);

  const htmlTag = html.match(/<html(?=[\s>])[^>]*>/i);
  if (htmlTag) return html.replace(htmlTag[0], `${htmlTag[0]}<head>${meta}</head>`);

  const doctype = html.match(/^\s*<!doctype[^>]*>/i); // Keep the doctype first to preserve standards-mode email layout.
  if (doctype) return html.replace(doctype[0], `${doctype[0]}<head>${meta}</head>`);

  return `${meta}${html}`;
}

const SnapshotModal: TOC<SnapshotModalSignature> = <template>
  <Modal
    @isOpen={{true}}
    @onClose={{@onClose}}
    @size="2xl"
    @ariaLabel={{t "common.profile.notification_histories.sent_email"}}
  >
    <:header>
      <span class="replay-mask">{{or @snapshot.sentSubject @row.noteSubtype}}</span>
    </:header>
    <:body>
      {{#if @row.hasSentSnapshot}}
        {{#if @isLoading}}
          <div
            class="tio-fg-muted tio-type-body-secondary py-8 text-center"
            data-test-snapshot-loading
          >{{t "common.profile.notification_histories.snapshot_loading"}}</div>
        {{else}}
          {{#let (sandboxSnapshotHtml @snapshot.sentBodyHtml) as |html|}}
            {{#if html}}
              <iframe
                title={{t "common.profile.notification_histories.sent_email"}}
                sandbox=""
                srcdoc={{html}}
                class="replay-block w-full h-96 rounded border-0"
                data-test-snapshot-frame
              ></iframe>
              <p class="tio-fg-muted tio-type-caption mt-2" data-test-snapshot-offline-notice>{{t
                  "common.profile.notification_histories.snapshot_offline_notice"
                }}</p>
            {{else}}
              <p class="tio-fg-muted tio-type-body-secondary" data-test-snapshot-unavailable>{{t
                  "common.profile.notification_histories.snapshot_unavailable"
                }}</p>
            {{/if}}
          {{/let}}
        {{/if}}
      {{else}}
        <p class="tio-fg-muted tio-type-body-secondary" data-test-no-snapshot-notice>{{t
            "common.profile.notification_histories.no_snapshot_notice"
          }}</p>
      {{/if}}
    </:body>
  </Modal>
</template>;

export default class NotificationSnapshotPreview extends Component<NotificationSnapshotPreviewSignature> {
  readonly query =
    this.args.row.hasSentSnapshot && this.args.row.id
      ? notificationSnapshotRequest(this.args.row.id)
      : null;

  <template>
    {{#if this.query}}
      <Request @query={{this.query}}>
        <:error>{{! Failed or forbidden reads render the unavailable state below. }}</:error>
        <:always as |state|>
          <SnapshotModal
            @row={{@row}}
            @onClose={{@onClose}}
            @snapshot={{state.value.data}}
            @isLoading={{state.isPending}}
          />
        </:always>
      </Request>
    {{else}}
      <SnapshotModal @row={{@row}} @onClose={{@onClose}} />
    {{/if}}
  </template>
}
